import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUserProfileStore } from '@/stores/userProfile'
import { authProvider } from '@/services/currentAuthProvider'
import { redirectToPortal } from '@/utils/app-redirect'
import { useFeatureAccess } from '@/composables/useFeatureAccess'
import { useJeugdwetEnablement } from '@/composables/jeugdwet/useJeugdwet'
import { useZorgMailInboxAvailability } from '@/composables/zorgmail/useZorgMailInboxAvailability'

/** Routes that require FULL license - FORMS_ONLY users are redirected to /formulieren */
const fullLicenseRoutes = ['/clients', '/agenda', '/declaraties', '/settings/signaleringen']

/**
 * Behandelaar app authentication guard
 *
 * Validates that:
 * 1. User is authenticated for protected routes
 * 2. User is NOT of type 'client' (clients should use the portal)
 * 3. User has correct license for the route (FORMS_ONLY cannot access full-license routes)
 * 4. User has required permissions for the route
 * 5. A Jeugdwet route is not reachable for an organisation without Jeugdwet enabled
 * 6. The ZorgMail inbox is not reachable before the practice's mailbox has synced once
 *
 * If a client tries to access the behandelaar app, they are redirected to mijn.scrivio.nl
 */
export async function behandelaarAuthGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const authStore = useAuthStore()

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const requiresJeugdwet = to.matched.some((record) => record.meta.requiresJeugdwet)
  const requiresZorgMailInbox = to.matched.some((record) => record.meta.requiresZorgMailInbox)
  // A route may name more than one permission, which means "any of" — the shape
  // an endpoint takes when reading it is not more privileged than any of the
  // features that produce it (the import preview, #3777).
  const requiredPermission = to.matched.find((record) => record.meta.requiredPermission)?.meta
    .requiredPermission as string | string[] | undefined

  // If route doesn't require auth, check for authenticated user trying to access login/register
  if (!requiresAuth) {
    if ((to.name === 'login' || to.name === 'register') && authStore.isAuthenticated) {
      return next({ name: 'dashboard' })
    }
    return next()
  }

  // Route requires auth - check if user is authenticated
  if (!authStore.isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  // User is authenticated - verify they are NOT a client user
  if (authProvider.currentSession()) {
    try {
      const claims = await authProvider.getTokenClaims()
      const userType = claims?.userType as string | undefined

      if (userType === 'client') {
        // Cancel navigation before redirecting, or the component mounts and its API calls 403
        next(false)
        redirectToPortal()
        return
      }
    } catch {
      // The user is authenticated; only their userType could not be read
      console.warn('[Auth] Could not verify user type, continuing with navigation')
    }
  }

  // Nothing past this point may read the user's permissions before they are known. A failed
  // load has already been reported by the store and leaves no permissions, which the checks
  // below answer by sending the user to the dashboard.
  const userProfileStore = useUserProfileStore()
  await userProfileStore.ensureLoaded().catch(() => undefined)

  // Check permission requirements first.
  // The full-license routes (clients, agenda, declaraties, signal settings) are all
  // guarded by a permission FORMS_ONLY users never hold (clients.read, appointments.read,
  // billing.read, ...), so this deterministically redirects them to the dashboard. Doing
  // it before the license check avoids depending on whether the organization's license
  // has finished loading: isFormsOnly is derived from the (non-persisted) organization
  // store, which races against this guard on a fresh page load.
  if (requiredPermission) {
    const userPermissions: readonly string[] = userProfileStore.permissions
    const accepted = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission]
    if (!accepted.some((permission) => userPermissions.includes(permission))) {
      return next({ name: 'dashboard' })
    }
  }

  // Check license-based route restrictions (ADR-023) as a secondary net for any
  // full-license route the user happens to hold the permission for but the license forbids.
  // Redirect to the form-submissions list (there is no route literally named 'formulieren';
  // '/formulieren' is an unnamed redirect to '/form-submissions').
  const { isFormsOnly } = useFeatureAccess()
  if (isFormsOnly.value && fullLicenseRoutes.some((route) => to.path.startsWith(route))) {
    return next({ name: 'form-submissions' })
  }

  // The organisation's own Jeugdwet enablement (#4697) — asked only here, once the user
  // is known to be signed in to one.
  if (requiresJeugdwet) {
    const { confirmed, ensureLoaded } = useJeugdwetEnablement()
    await ensureLoaded()
    if (!confirmed.value.jeugdwet) {
      return next({ name: 'dashboard' })
    }
  }

  if (requiresZorgMailInbox) {
    const { inboxAvailable, ensureLoaded } = useZorgMailInboxAvailability()
    await ensureLoaded()
    if (!inboxAvailable.value) {
      return next({ name: 'dashboard' })
    }
  }

  // User is authenticated and is NOT a client - allow access
  next()
}
